import { t } from '../i18n';
import { SITE } from '../site';

/**
 * Cada petición dice de qué dashboard viene: la API lee la sesión de esa marca y responde solo con
 * lo suyo (los mensajes, los usuarios, la portada…).
 */
export const SITE_HEADER = 'X-Dashboard-Site';

/** Error de la API con los mensajes por campo, si los hay. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly errors: { path: string; message: string }[] = [],
  ) {
    super(message);
  }

  /** Mensaje del campo `path` (o de uno de sus hijos). */
  field(path: string): string | undefined {
    return this.errors.find((e) => e.path === path || e.path.startsWith(`${path}.`))?.message;
  }
}

let onUnauthorized: (() => void) | undefined;
/** Qué hacer cuando la sesión caduca (lo define el proveedor de sesión). */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/** fetch a /api con JSON y errores legibles. La cookie de sesión va sola (mismo origen). */
export async function api<T>(path: string, { method = 'GET', body, signal }: Options = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    signal,
    credentials: 'same-origin',
    headers: { [SITE_HEADER]: SITE, ...(body !== undefined && { 'Content-Type': 'application/json' }) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => undefined)) as
    | (T & { message?: string | string[]; errors?: { path: string; message: string }[] })
    | undefined;
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/admin/auth/login')) onUnauthorized?.();
    const message = Array.isArray(data?.message) ? data.message.join('. ') : (data?.message ?? `Error ${res.status}`);
    throw new ApiError(res.status, message, data?.errors ?? []);
  }
  return data as T;
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>(path, { signal });
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body });
export const put = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body });
export const patch = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body });
export const del = <T = void>(path: string) => api<T>(path, { method: 'DELETE' });

/** Mensaje para mostrar a partir de cualquier error. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return t('Algo salió mal');
}
